export type WasteMovementFormValues = {
    siteId: string;
    movementDate: string;
    weightTonnes: string;
    reference: string;
    regEwcCodeId: string;
    originGeographyId: string;
    destinationGeographyId: string;
    regDisposalRecoveryCodeId: string;
    regPhysicalStateId: string;
    municipalFlagId: string;
    degradableFlagId: string;
    regSourceActivityId: string;
    regPreTreatmentId: string;
    notes: string;
    /** Receipt capture (Received only). "" = not captured; yes/no selects hold "" | "true" | "false". */
    receivedTime: string;
    /** "earlier" | "later": which occurrence of a UK time that repeats when the clocks go back; "" otherwise. */
    receivedTimeOccurrence: string;
    wasteDescription: string;
    containerTypeCode: string;
    numberOfContainers: string;
    weightIsEstimate: string;
    containsHazardous: string;
    containsPops: string;
    carrierHaulierId: string;
    carrierOrganisationName: string;
    carrierRegistrationNumber: string;
    carrierReasonForNoRegistrationNumber: string;
    carrierMeansOfTransport: string;
    carrierVehicleId: string;
    carrierVehicleRegistration: string;
    /** S1C (Received only). Sources are DEFRA codes or ""; concentrations are the text the operator typed (mg/kg). */
    hazardousSourceOfComponents: string;
    hazardousPropertyCodes: string[];
    hazardousComponents: WasteMovementHazardousComponentFormRow[];
    popsSourceOfComponents: string;
    popComponents: WasteMovementPopComponentFormRow[];
    hazardousWasteConsignmentCode: string;
    reasonForNoConsignmentCode: string;
    /** Deliberate confirmation that stored hazardous / POP detail is removed after answering No. */
    confirmRemoveHazardousDetail: boolean;
    confirmRemovePopsDetail: boolean;
    /**
     * S1D (Received only). "Was a broker or dealer involved?" as "" | "true" | "false": a form answer only (a saved
     * broker/dealer means Yes); the six values are sent only while it is Yes.
     */
    brokerDealerInvolved: string;
    brokerDealerOrganisationName: string;
    brokerDealerRegistrationNumber: string;
    brokerDealerFullAddress: string;
    brokerDealerPostcode: string;
    brokerDealerEmailAddress: string;
    brokerDealerPhoneNumber: string;
    /** Deliberate confirmation that the saved broker/dealer is removed after answering No. */
    confirmRemoveBrokerDealer: boolean;
};

/** Form fields holding a single string: the only ones site defaults and EWC default rules pre-fill. */
export type WasteMovementStringFieldKey = {
    [K in keyof WasteMovementFormValues]: WasteMovementFormValues[K] extends string ? K : never;
}[keyof WasteMovementFormValues];

export type WasteMovementHazardousComponentFormRow = { name: string; concentration: string };
export type WasteMovementPopComponentFormRow = { code: string; concentration: string };

export type WasteMovementTriState = "" | "true" | "false";

export type WasteMovementReceiptCaptureFormValues = Pick<
    WasteMovementFormValues,
    | "receivedTime"
    | "receivedTimeOccurrence"
    | "wasteDescription"
    | "containerTypeCode"
    | "numberOfContainers"
    | "weightIsEstimate"
    | "containsHazardous"
    | "containsPops"
    | "carrierHaulierId"
    | "carrierOrganisationName"
    | "carrierRegistrationNumber"
    | "carrierReasonForNoRegistrationNumber"
    | "carrierMeansOfTransport"
    | "carrierVehicleId"
    | "carrierVehicleRegistration"
>;

export type WasteMovementHazardousPopsFormValues = Pick<
    WasteMovementFormValues,
    | "hazardousSourceOfComponents"
    | "hazardousPropertyCodes"
    | "hazardousComponents"
    | "popsSourceOfComponents"
    | "popComponents"
    | "hazardousWasteConsignmentCode"
    | "reasonForNoConsignmentCode"
    | "confirmRemoveHazardousDetail"
    | "confirmRemovePopsDetail"
>;

export type WasteMovementBrokerDealerFormValues = Pick<
    WasteMovementFormValues,
    | "brokerDealerInvolved"
    | "brokerDealerOrganisationName"
    | "brokerDealerRegistrationNumber"
    | "brokerDealerFullAddress"
    | "brokerDealerPostcode"
    | "brokerDealerEmailAddress"
    | "brokerDealerPhoneNumber"
    | "confirmRemoveBrokerDealer"
>;

/** S1D form fields (Received only). The confirmation flag is UI state, never a capture value on its own. */
export const WASTE_MOVEMENT_BROKER_DEALER_FIELDS: readonly (keyof WasteMovementBrokerDealerFormValues)[] = [
    "brokerDealerInvolved",
    "brokerDealerOrganisationName",
    "brokerDealerRegistrationNumber",
    "brokerDealerFullAddress",
    "brokerDealerPostcode",
    "brokerDealerEmailAddress",
    "brokerDealerPhoneNumber",
    "confirmRemoveBrokerDealer",
];

/** S1C form fields (Received only). The two confirmation flags are UI state, never a capture value on their own. */
export const WASTE_MOVEMENT_HAZARDOUS_POPS_FIELDS: readonly (keyof WasteMovementHazardousPopsFormValues)[] = [
    "hazardousSourceOfComponents",
    "hazardousPropertyCodes",
    "hazardousComponents",
    "popsSourceOfComponents",
    "popComponents",
    "hazardousWasteConsignmentCode",
    "reasonForNoConsignmentCode",
    "confirmRemoveHazardousDetail",
    "confirmRemovePopsDetail",
];

export const WASTE_MOVEMENT_RECEIPT_CAPTURE_FIELDS: readonly (keyof WasteMovementReceiptCaptureFormValues)[] = [
    "receivedTime",
    "receivedTimeOccurrence",
    "wasteDescription",
    "containerTypeCode",
    "numberOfContainers",
    "weightIsEstimate",
    "containsHazardous",
    "containsPops",
    "carrierHaulierId",
    "carrierOrganisationName",
    "carrierRegistrationNumber",
    "carrierReasonForNoRegistrationNumber",
    "carrierMeansOfTransport",
    "carrierVehicleId",
    "carrierVehicleRegistration",
];
